import { createTheme } from '@mui/material/styles';

const tokens = {
  zinc50: '#fafafa',
  zinc100: '#f4f4f5',
  zinc200: '#e4e4e7',
  zinc300: '#d4d4d8',
  zinc400: '#a1a1aa',
  zinc500: '#71717a',
  zinc600: '#52525b',
  zinc700: '#3f3f46',
  zinc900: '#18181b',
  zinc950: '#09090b',

  blue50: '#e6eff7',
  blue200: '#9ec4e3',
  blue600: '#0055a5',
  blue700: '#004485',

  amber50: '#fffbeb',
  amber200: '#fde68a',
  amber500: '#f59e0b',
  amber700: '#b45309',

  red50: '#fef2f2',
  red200: '#fecaca',
  red600: '#dc2626',
  red700: '#b91c1c',

  green50: '#f0fdf4',
  green200: '#bbf7d0',
  green700: '#15803d'
};

const fontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.blue600,
      dark: tokens.blue700,
      light: tokens.blue50,
      contrastText: '#ffffff'
    },
    error: {
      main: tokens.red600,
      dark: tokens.red700,
      light: tokens.red50,
      contrastText: '#ffffff'
    },
    warning: {
      main: tokens.amber500,
      dark: tokens.amber700,
      light: tokens.amber50,
      contrastText: '#ffffff'
    },
    success: {
      main: tokens.green700,
      light: tokens.green50,
      contrastText: '#ffffff'
    },
    text: {
      primary: tokens.zinc900,
      secondary: tokens.zinc500,
      disabled: tokens.zinc400
    },
    divider: tokens.zinc200,
    background: {
      default: tokens.zinc50,
      paper: '#ffffff'
    }
  },

  typography: {
    fontFamily,
    h1: { fontFamily, fontWeight: 600, fontSize: '1.875rem', color: tokens.zinc950 },
    h2: { fontFamily, fontWeight: 600, fontSize: '1.5rem', color: tokens.zinc950 },
    h3: { fontFamily, fontWeight: 600, fontSize: '1.25rem', color: tokens.zinc950 },
    h4: { fontFamily, fontWeight: 600, fontSize: '1.125rem', color: tokens.zinc950 },
    h5: { fontFamily, fontWeight: 600, fontSize: '1rem', color: tokens.zinc950 },
    h6: { fontFamily, fontWeight: 600, fontSize: '0.875rem', color: tokens.zinc950 },
    body1: { fontFamily, fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.5, color: tokens.zinc900 },
    body2: { fontFamily, fontWeight: 400, fontSize: '0.8125rem', lineHeight: 1.5, color: tokens.zinc900 },
    subtitle1: { fontFamily, fontWeight: 500, fontSize: '0.875rem', color: tokens.zinc900 },
    subtitle2: { fontFamily, fontWeight: 500, fontSize: '0.8125rem', color: tokens.zinc900 },
    button: { fontFamily, fontWeight: 500, fontSize: '0.875rem', textTransform: 'none' },
    caption: { fontFamily, fontWeight: 400, fontSize: '0.75rem', color: tokens.zinc400 }
  },

  shape: {
    borderRadius: 6
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale'
        }
      }
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          border: `1px solid ${tokens.zinc200}`,
          backgroundImage: 'none'
        }
      }
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          border: `1px solid ${tokens.zinc200}`,
          backgroundImage: 'none'
        }
      }
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: tokens.blue600,
          color: '#ffffff',
          boxShadow: 'none',
          border: 'none',
          borderRadius: 0,
          backgroundImage: 'none',
          '& .MuiTypography-root': { color: '#ffffff' },
          '& .MuiButton-root': {
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#ffffff'
            }
          },
          '& .MuiIconButton-root': {
            color: '#ffffff',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
          },
          '& .MuiLink-root': { color: '#ffffff' }
        }
      }
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 500,
          padding: '6px 12px',
          minHeight: 32,
          boxShadow: 'none'
        },
        containedPrimary: {
          backgroundColor: tokens.blue600,
          color: '#ffffff',
          '&:hover': { backgroundColor: tokens.blue700 }
        },
        outlinedPrimary: {
          borderColor: tokens.zinc200,
          color: tokens.zinc900,
          '&:hover': { backgroundColor: tokens.zinc50, borderColor: tokens.zinc300 }
        },
        text: {
          color: tokens.blue600,
          '&:hover': { backgroundColor: tokens.blue50, color: tokens.blue700 }
        }
      }
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: 6,
          '&:hover': { backgroundColor: tokens.zinc100 }
        }
      }
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.zinc200 },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tokens.zinc300 },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.blue600,
            borderWidth: 1
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(0, 85, 165, 0.15)'
          }
        },
        input: {
          padding: '8px 12px',
          fontSize: '0.875rem',
          color: tokens.zinc900
        }
      }
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          color: tokens.zinc500,
          '&.Mui-focused': { color: tokens.blue600 },
          '&.MuiInputLabel-outlined:not(.Mui-focused):not(.MuiInputLabel-shrink)': {
            transform: 'translate(14px, 9px)'
          }
        }
      }
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: '0.875rem',
          color: tokens.zinc900
        }
      }
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: tokens.zinc900,
          '&:hover': { backgroundColor: tokens.zinc100 },
          '&.Mui-selected': {
            backgroundColor: tokens.blue50,
            color: tokens.blue700,
            '&:hover': { backgroundColor: tokens.blue50 }
          }
        }
      }
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
          fontSize: '0.75rem',
          fontWeight: 500,
          height: 22,
          border: `1px solid ${tokens.zinc200}`,
          backgroundColor: tokens.zinc100,
          color: tokens.zinc700
        },
        colorPrimary: {
          backgroundColor: tokens.blue50,
          color: tokens.blue700,
          border: `1px solid ${tokens.blue200}`,
          '& .MuiChip-label': { color: tokens.blue700 }
        },
        colorError: {
          backgroundColor: tokens.red50,
          color: tokens.red700,
          border: `1px solid ${tokens.red200}`
        },
        colorWarning: {
          backgroundColor: tokens.amber50,
          color: tokens.amber700,
          border: `1px solid ${tokens.amber200}`
        },
        colorSuccess: {
          backgroundColor: tokens.green50,
          color: tokens.green700,
          border: `1px solid ${tokens.green200}`
        },
        label: { fontWeight: 500 }
      }
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px 12px',
          fontSize: '0.875rem',
          color: tokens.zinc900,
          borderBottom: `1px solid ${tokens.zinc100}`
        },
        head: {
          fontSize: '0.75rem',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: tokens.zinc500,
          borderBottom: `1px solid ${tokens.zinc200}`
        }
      }
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: tokens.zinc50 }
        }
      }
    },

    MuiTableContainer: {
      styleOverrides: {
        root: { borderRadius: 'inherit' }
      }
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${tokens.zinc200}`,
          backgroundImage: 'none'
        }
      }
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          fontWeight: 600,
          padding: '16px 20px'
        }
      }
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.875rem',
          boxShadow: 'none',
          border: '1px solid'
        },
        standardError: {
          backgroundColor: tokens.red50,
          color: tokens.red700,
          borderColor: tokens.red200
        },
        standardWarning: {
          backgroundColor: tokens.amber50,
          color: tokens.amber700,
          borderColor: tokens.amber200
        },
        standardInfo: {
          backgroundColor: tokens.blue50,
          color: tokens.blue700,
          borderColor: tokens.blue200
        },
        standardSuccess: {
          backgroundColor: tokens.green50,
          color: tokens.green700,
          borderColor: tokens.green200
        }
      }
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.zinc900,
          color: '#ffffff',
          fontSize: '0.75rem',
          fontWeight: 400,
          padding: '6px 10px',
          borderRadius: 6
        },
        arrow: {
          color: tokens.zinc900
        }
      }
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 44,
          height: 24,
          padding: 4,
          overflow: 'visible'
        },
        switchBase: {
          padding: 5,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: '#ffffff',
            '& + .MuiSwitch-track': {
              backgroundColor: tokens.blue600,
              opacity: 1
            }
          }
        },
        thumb: {
          width: 14,
          height: 14,
          boxShadow: 'none'
        },
        track: {
          borderRadius: 9999,
          backgroundColor: tokens.zinc300,
          opacity: 1
        }
      }
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: tokens.blue600,
          textDecoration: 'none',
          '&:hover': {
            color: tokens.blue700,
            textDecoration: 'underline'
          }
        }
      }
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: tokens.zinc200 }
      }
    }
  }
});
